/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import Grid from '@material-ui/core/Grid';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import TextField from '@material-ui/core/TextField';
import {
    func, string, number, object, bool,
} from 'prop-types';
import DateFnsUtils from '@date-io/moment';
import withStyles from '@material-ui/core/styles/withStyles';
import React, { useState, useEffect, useRef } from 'react';
import usePersistedState from '../usePersistState';
import Button from '../Button/Button';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { getClientDate, isPast } from '../static_data/utils/dateUtils';
import { ENUMS } from '../static_data/enum';

const useStyles = () => ({
    DatePickerContainer: {
        margin: 10,
        padding: '0 15px 0 0',
    },
    PlantsDatePickerContainer: {
        margin: 0,
        padding: 0,
    },
    DatePickerContainerError: {
        margin: '10px 10px 0 10px',
        padding: '0 15px 0 0',
    },
    ErrorMessageContainer: {
        padding: '0 0 5px 10px',
    },
    NoErrorMessageContainer: {
        padding: '0 0 0 10px',
    },
    DatePickerInput: {
        fontSize: 15,
        margin: '0 0 8px 0',
    },
    PlantsDatePickerInput: {
        fontSize: '1rem',
        margin: '0 5px 0',
        color: '#707070',
    },
    DatePickerFormControl: {
        height: 25,
        margin: '0 0 4px 0',
    },
    DatePickerButton: {
        margin: '0 0 8px 0',
    },
    PlantsDatePickerButton: {
        margin: '0',
    },
    DatePickerUnderline: {
        margin: '0 0 1px 0',
    },
});

const useMountEffect = (fun, args) => useEffect(fun, args);

function DatePicker(props) {
    const {
        classes, index, label, helperText, isPlants, handleChange,
    } = props;
    const [selectedDate, setSelectedDate] = useState(null);
    const [currentDate, setCurrentDate] = useState(null);
    const [inputFields, setInputFields] = usePersistedState(
        `twilio-chat-input-${index}`,
        {
            error: false,
            label,
            helperText,
            isDisabled: false,
            isTyping: false,
        },
    );

    const datePickerRef = useRef(null);

    const openNativeDatePicker = () => {
        if (datePickerRef.current) {
            datePickerRef.current.showPicker();
        }
    };

    useMountEffect(() => {
        const clientDate = getClientDate(props.timeZone);
        setCurrentDate(clientDate);
        setSelectedDate(new Date().toISOString()?.split?.('T')?.[0]);
    }, []);

    useMountEffect(() => {
        props.scrollToBottom();
    }, [inputFields.error]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
        setInputFields({
            ...inputFields,
            error: false,
            isTyping: true,
        });
        if (isPlants) {
            handleChange(e);
        }
    };

    const validate = () => {
        if (selectedDate.isValid() && !isPast(selectedDate, currentDate)) {
            props.sendMessage(selectedDate.format(ENUMS.COMMON_DATE_FORMAT));
            setInputFields({
                ...inputFields,
                error: false,
                isDisabled: true,
                isTyping: false,
            });
        } else {
            setInputFields({
                ...inputFields,
                error: true,
            });
        }
    };

    const onClick = () => {
        validate();
    };

    const onKeyPress = (event) => {
        if (event.key === 'Enter') {
            validate();
            event.preventDefault();
        }
    };

    let PickerContainer = classes.DatePickerContainer;
    let PickerInput = classes.DatePickerInput;
    let PickerButton = classes.DatePickerButton;

    if (isPlants) {
        PickerContainer = classes.PlantsDatePickerContainer;
        PickerInput = classes.PlantsDatePickerInput;
        PickerButton = classes.PlantsDatePickerButton;
    }
    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid
                container
                className={
                    inputFields.error
                        ? classes.DatePickerContainerError
                        : PickerContainer
                }
                alignItems="center"
            >
                <Grid item xs={isPlants ? 12 : 9}>
                    <TextField
                        type="date"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        variant="outlined"
                        name="date"
                        fullWidth={isPlants}
                        animateYearScrolling
                        id="date-picker-inline"
                        label={isPlants ? label : inputFields.label}
                        error={inputFields.error}
                        helperText=""
                        value={selectedDate}
                        minDate={currentDate}
                        onChange={handleDateChange}
                        onKeyPress={(e) => onKeyPress(e)}
                        readOnly={inputFields.isDisabled}
                        KeyboardButtonProps={{
                            classes: {
                                root: PickerButton,
                            },
                        }}
                        InputProps={{
                            readOnly: inputFields.isDisabled,
                            classes: {
                                formControl: classes.DatePickerFormControl,
                                input: PickerInput,
                                underline: classes.DatePickerUnderline,
                            },
                            style: { height: '55px' },
                        }}
                        inputRef={datePickerRef}
                        onClick={openNativeDatePicker}
                    />
                </Grid>
                {!isPlants && (
                    <Grid item xs={3}>
                        <Button
                            onClick={onClick}
                            error={inputFields.error}
                            isDisabled={inputFields.isDisabled}
                            isTyping={inputFields.isTyping}
                        />
                    </Grid>
                )}
            </Grid>
            <Grid
                item
                xs
                className={
                    inputFields.error
                        ? classes.ErrorMessageContainer
                        : classes.NoErrorMessageContainer
                }
            >
                <ErrorMessage
                    error={inputFields.error}
                    helperText={inputFields.helperText}
                />
            </Grid>
        </MuiPickersUtilsProvider>
    );
}

DatePicker.propTypes = {
    label: string.isRequired,
    classes: object.isRequired,
    helperText: string.isRequired,
    index: number.isRequired,
    sendMessage: func.isRequired,
    scrollToBottom: func.isRequired,
    timeZone: func.isRequired,
    isPlants: bool,
    handleChange: func,
};

DatePicker.defaultProps = {
    isPlants: false,
    handleChange: () => {},
};

export default withStyles(useStyles)(DatePicker);
